import { prioritizeOutlook } from "@revelate/calc/target-outlook";
import { getOutlookRiskMemberships } from "../target-outlook/risks";
import { selectOutlookDrivers } from "../target-outlook/view";
import type { ForecastInsightDeal } from "../state-of-sales-v2/forecastInsights";
import type { PipelineHealthData } from "./presentation";
import { OUTLOOK_COPY } from "../target-outlook/copy";

export function verifiedManagementStory({ position, story, model }: PipelineHealthData) {
  return !model.isLoading && !model.error && !model.forecastEnrichmentLoading
    && position && Number.isFinite(position.forecast) && story?.dataMode === "live"
    && story.currencyCode === model.companyCurrency?.code && story.expectedOutcome === position.forecast
    && story.period?.toLowerCase() === OUTLOOK_COPY.periods[model.period]?.replace(/^the /iu, "").toLowerCase()
    && story.target === (Number.isFinite(position.target) && position.target > 0 ? position.target : null)
    && story.deals.every(deal => Number.isFinite(deal.evidenceContribution) && Number.isFinite(deal.baselineContribution))
    && new Set(story.deals.map(deal => deal.id)).size === story.deals.length ? story : null;
}

/** Read-only reuse of Target Outlook's period-sales materiality and non-overlapping
 * expected-contribution exposure. Never apply this threshold to gross creation,
 * consume its full-Forecast priority, or value warnings as predicted losses.
 * Missing/partial/stale risk evaluation is unknown, not zero exposure. */
export function managementExposure(data: PipelineHealthData, openDeals: ForecastInsightDeal[] | null) {
  const { position } = data;
  const story = verifiedManagementStory(data);
  const evaluatedIds = new Set(openDeals?.map(deal => String(deal.id)));
  const ready = openDeals !== null
    && position && !position.historical && Number.isFinite(position.forecast) && position.target > 0 && Number.isFinite(position.target)
    && story && story.deals.filter(deal => deal.evidenceContribution > 0).every(deal => evaluatedIds.has(deal.id))
    && (position.expectedFromOpenDeals <= 0 || openDeals.length > 0 && story.deals.some(deal => deal.evidenceContribution > 0));
  if (!ready) return { ready: false, minimumImpact: null, exposures: [], primary: null };
  const minimumImpact = prioritizeOutlook({ current: position.forecast, target: position.target,
    closed: false, futurePeriod: data.range.start > data.asOf, futureIncluded: false,
    daysRemaining: 0, cycle: null, paths: [], newCreation: null, downsideDelta: null }).minimumImpact;
  const exposures = selectOutlookDrivers(story, minimumImpact, getOutlookRiskMemberships(story, openDeals))
    .filter(driver => driver.kind === "exposure").toSorted((a, b) => b.amount - a.amount);
  return { ready: true, minimumImpact, exposures, primary: exposures[0] ?? null };
}
export type ManagementExposure = ReturnType<typeof managementExposure>;
